/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import Loader from "../../Loader/Loader"
import { useMsal } from "@azure/msal-react"
import CloseIcon from '@mui/icons-material/Close'
import { FormControlLabel, IconButton, Radio, RadioGroup, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { toast } from "react-hot-toast"
import { config } from "../../../../AppConfig/config"
import { loginRequest, requestIdToken } from "../../../../auth/authConfig"
import VAR_COMMON_CONSTANTS from "../../../../constants/globalVariables"
import { getCoverageRequirementsSelectedDatasetId } from "../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/CoverageRequirements"
import { downloadDatasetAsCSV } from "../../../pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/constants"
import { MERGED_DATASET_TYPE_STR } from "../../../pages/CoverageRequirements/ItemDetails/constants"
import ArgosButton from "../../Buttons/ArgosButton"
import ArgosFlexbox from "../../Containers/ArgosFlexbox"
import ArgosCheckbox from "../../Form/Checkbox/ArgosCheckbox"
import ArgosTextField from "../../Form/TextField/ArgosTextField"
import ArgosDialogHeader from "../ArgosDialogHeader"

const enum EDownloadFileTypes {
  LINE = 'Line',
  DOE_OUTPUT = 'DOE Output',
}

const enum EFileSuffix {
  RED = '_red',
  RAW = '_raw',
  DOE_OUTPUT = '_doe_output'
}

const DownloadDatasetsDialog = (props: any) => {
  const {instance, accounts} = useMsal()
  const datasetId = getCoverageRequirementsSelectedDatasetId()
  const [error, setError] = useState("");
  const baseFileName = props.breadcrumbs.join('-').replaceAll(" ", "-")
  const [downloadFileName, setDownloadFileName] = useState<string>(baseFileName + EFileSuffix.RED + EFileSuffix.RAW)

  /**
   * Checkbox state
   */
  const [checkedRed, setCheckedRed] = useState(true);
  const [checkedRaw, setCheckedRaw] = useState(true);
  const handleChangeRed = () => {
    setCheckedRed(!checkedRed);
  };
  const handleChangeRaw = () => {
    setCheckedRaw(!checkedRaw);
  };

  /**
   * Radio button state
   */
  const DEFAULT_RADIO_VALUE = EDownloadFileTypes.LINE;
  const [radioValue, setRadioValue] = useState(DEFAULT_RADIO_VALUE);
  const handleRadioChange = (event: any) => {
    setRadioValue(event.target.value);
  };
  const disableLineCheckboxes = radioValue !== EDownloadFileTypes.LINE;

  /**
   * Update download file name based on settings
   */
  useEffect(() => {
    let suffix = "";

    switch (radioValue) {
      case EDownloadFileTypes.LINE: {
        if (checkedRed)
          suffix += EFileSuffix.RED;
        if (checkedRaw)
          suffix += EFileSuffix.RAW;
        break;
      }
      case EDownloadFileTypes.DOE_OUTPUT:
        suffix += EFileSuffix.DOE_OUTPUT;
        break;
    }

    const _fileName = baseFileName + suffix;
    setDownloadFileName(_fileName);
  }, [checkedRed, checkedRaw, radioValue, baseFileName]);

  const handleDownloadDatasetFile = async (_downloadFileName: any, red: boolean, raw: boolean, doeOutput: boolean = false) => {
    let url = `${config.url.API_URL}${downloadDatasetAsCSV}`
    const idTok = await requestIdToken(instance, accounts);
    const loadingToastId = toast.loading('Downloading ' + _downloadFileName + ".csv", {
      icon: <Loader loaderSize={25} isLoading={true}/>,
    });
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": `Bearer ${idTok}`
      },
      body: JSON.stringify({
        "fileName": _downloadFileName,
        "selectedDatasetId": datasetId,
        "red": red,
        "raw": raw,
        doeOutput
      })
    }).then((data) => {
      if (data.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
          console.log(e);
        });

      data.blob().then((blob: any) => {
        const fileURL = window.URL.createObjectURL(blob)
        let alink = document.createElement('a');
        alink.href = fileURL;
        alink.download = `${_downloadFileName}.csv`;
        alink.click();
      })
    })
      .catch((e) => {
        console.log(e)
        toast.error("An error ocurred when downloading your data")
      }).finally(() => {
        toast.dismiss(loadingToastId); // Dismiss the loading toast
      })
  }
  const onChange = (e:any) => {
    const newValue = e.target.value;

    if (!newValue.match(VAR_COMMON_CONSTANTS.VAR_FORBIDDEN_CHARS)) {
      setError("");
    } else {
      setError(VAR_COMMON_CONSTANTS.VAR_FORBIDDEN_CHARS_MSG);
    }
    setDownloadFileName(newValue.replaceAll(" ", ""))
  };
  return (
    <ArgosFlexbox vertical>
      {/*Dialog header*/}
      <div style={{display: 'flex', flexDirection: 'column'}}>
        <div>
          <ArgosDialogHeader>
            <ArgosFlexbox style={{justifyContent: 'space-between', alignItems: 'center'}}>
              <Typography sx={{color: 'white', fontSize: 14}}>Download {VAR_COMMON_CONSTANTS.VAR_DATA_SET} as CSV</Typography>
              <IconButton onClick={() => props.onClose()} sx={{color: 'white', fontSize: 'small', padding: 0}}>
                <CloseIcon/>
              </IconButton>
            </ArgosFlexbox>
          </ArgosDialogHeader>
        </div>
      </div>

      {/*Dialog content*/}
      <ArgosFlexbox vertical style={{alignItems: "left", padding: '0 10px 10px 10px'}}>
        {/*Radio Buttons*/}
        <RadioGroup
          defaultValue={DEFAULT_RADIO_VALUE}
          value={radioValue}
          onChange={handleRadioChange}
        >
          {/*Line radio button*/}
          <ArgosFlexbox>
            <FormControlLabel value={EDownloadFileTypes.LINE} control={<Radio/>} label={EDownloadFileTypes.LINE}/>
            <ArgosFlexbox alignItems={'center'}>
              <ArgosCheckbox
                checked={checkedRed}
                onChange={handleChangeRed}
                disabled={disableLineCheckboxes}
              /><Typography>Redline</Typography>
              <ArgosCheckbox
                checked={checkedRaw}
                onChange={handleChangeRaw}
                disabled={disableLineCheckboxes}
              /><Typography>Raw</Typography>
            </ArgosFlexbox>
          </ArgosFlexbox>

          {/*DOE Output radio button*/}
          {props.datasetType != MERGED_DATASET_TYPE_STR &&
            <ArgosFlexbox>
              <FormControlLabel value={EDownloadFileTypes.DOE_OUTPUT} control={<Radio/>} label={EDownloadFileTypes.DOE_OUTPUT}/>
            </ArgosFlexbox>
          }
        </RadioGroup>

        {/*Filename input*/}
        <ArgosFlexbox style={{alignItems: "center"}}>
          <Typography>
            Filename:
          </Typography>
          <ArgosTextField
            style={{width: `${downloadFileName.length + 10}ch`}}
            value={downloadFileName}
            onChange={onChange}
            helperText={error}
            error={!!error}
          >
          </ArgosTextField>
        </ArgosFlexbox>

        {/*Footer*/}
        <ArgosFlexbox vertical style={{alignItems: 'left'}}>
          <ArgosFlexbox>
            <ArgosButton disabled={(downloadFileName.trim() && (checkedRed || checkedRaw) ? false : true) || !!error} onClick={() => {
              // props?.downloadDatasetAsCSV(downloadFileName, checkedRed, checkedRaw)
              if (radioValue === EDownloadFileTypes.LINE) {
                handleDownloadDatasetFile(downloadFileName, checkedRed, checkedRaw)
              } else if (radioValue === EDownloadFileTypes.DOE_OUTPUT) {
                handleDownloadDatasetFile(downloadFileName, false, false, true)
              }
              props.onClose()
            }}>
              Download
            </ArgosButton>
            <ArgosButton onClick={() => props.onClose()}>
              Cancel
            </ArgosButton>
          </ArgosFlexbox>
        </ArgosFlexbox>
      </ArgosFlexbox>
    </ArgosFlexbox>
  )
}
export { DownloadDatasetsDialog }
